import { CitationFigure } from './citationSource'

/**
 * Turns a source's raw text (an OCR'd slide chunk, a page extract, a web
 * excerpt) into a short quote a reader can follow, and finds which figure a
 * sentence cited. Generic over any document: it reads text shape only (prose
 * versus number runs, boilerplate footers, math markup), never a source,
 * language or wording.
 */

/** About four lines of the hover card. */
export const QUOTE_MAX_CHARS = 280

/** A block shorter than this is a heading or a chart label, not a passage. */
const MIN_PASSAGE_WORDS = 6

/** How far below a passage its figures (a slide's number column) may sit. */
const FIGURE_ZONE_LINES = 16

const MAX_FIGURE_GROUPS = 3

const ELLIPSIS = '…'

// Unicode classes go through `new RegExp`: the es5 compile target rejects a `u` literal.
const WORD_RE = new RegExp("\\p{L}[\\p{L}\\p{M}'’]*", 'gu')
const LETTER_RE = new RegExp('\\p{L}', 'gu')
const LOWER_START_RE = new RegExp('^[\\p{Ll}(]', 'u')
const UPPER_START_RE = new RegExp('^["“(]?\\p{Lu}', 'u')
const NUMBER_RE = /\d+(?:[.,]\d+)*/g
const BOILERPLATE_RE =
  /©|\(c\)\s*\d{4}|copyright|\ball rights\b|\bconfidential\b|\bproprietary\b|^(?:source|sources|fuente|fuentes|fonte|fontes)\s*:/i
const LATEX_MATH_RE = /\$[^$\n]*[\\^{}_][^$\n]*\$/g
const ESCAPED_SYMBOL_RE = /\\([$%&#_])/g
const BULLET_RE = /^\s*(?:[•●▪■◦▶►]\s*|[-*+]\s+|#{1,6}\s+)/
const INLINE_HEADING_RE = /(^|\s)#{1,6}\s+/g
const EMPHASIS_RE = /\*\*|__/g
const TERMINAL_RE = /[.!?:;…]["'”’)]*$/
const SENTENCE_SPLIT_RE = new RegExp('(?<=[.!?…])\\s+(?=["“(]?\\p{Lu})', 'u')
const LIST_MARKER_RE = /^\s*(?:[-*+]|\d+[.)])\s+/
const CITE_LINK_RE = /\[[^\]]*\]\(#cite-[^)]*\)/g
const PERCENT_MENTION_RE = /(\d+(?:[.,]\d+)?)\s?(?:%|\bpercent\b|\bper cent\b|\bpor ciento\b|\bpour cent\b|\bpor cento\b)/gi

interface Passage {
  text: string
  firstLine: number
  lastLine: number
}

function words(text: string): Array<string> {
  return (text.match(WORD_RE) ?? []).filter((word) => word.length >= 2)
}

function letterRatio(text: string): number {
  const compact = text.replace(/\s+/g, '')
  if (!compact) {
    return 0
  }
  return (text.match(LETTER_RE) ?? []).length / compact.length
}

/** One omission mark where the source's "..." and our own cut meet. */
function tidy(quote: string): string {
  return quote.replace(/\.{3}/g, ELLIPSIS).replace(new RegExp(`${ELLIPSIS}(?:\\s*${ELLIPSIS})+`, 'g'), ELLIPSIS).trim()
}

function cleanLine(line: string): string {
  return line
    .replace(LATEX_MATH_RE, ' ')
    .replace(ESCAPED_SYMBOL_RE, '$1')
    .replace(BULLET_RE, '')
    .replace(INLINE_HEADING_RE, '$1')
    .replace(EMPHASIS_RE, '')
    .replace(/\s+/g, ' ')
    .trim()
}

function isBoilerplate(line: string): boolean {
  return BOILERPLATE_RE.test(line)
}

/** Running text: several words, mostly letters, not a table row. */
function isProse(line: string): boolean {
  return !line.includes('|') && !isBoilerplate(line) && words(line).length >= 3 && letterRatio(line) >= 0.55
}

/**
 * A wrapped line that carries on the sentence above it ("(tráfico) disminuye
 * -2.0%"). A new bullet, or a short capitalised line (a heading), starts over.
 */
function continues(previous: string, line: string, isBullet: boolean): boolean {
  if (isBullet || TERMINAL_RE.test(previous) || line.includes('|') || isBoilerplate(line) || words(line).length === 0) {
    return false
  }
  if (LOWER_START_RE.test(line)) {
    return true
  }
  return isProse(line) && words(line).length >= 5
}

function passagesOf(lines: Array<string>, bullets: Array<boolean>): Array<Passage> {
  const passages: Array<Passage> = []
  let current: Passage | null = null
  let previous = ''
  lines.forEach((line, index) => {
    if (current && line && continues(previous, line, bullets[index])) {
      current.text = `${current.text} ${line}`
      current.lastLine = index
      previous = line
      return
    }
    if (current) {
      passages.push(current)
      current = null
    }
    if (line && isProse(line)) {
      current = { text: line, firstLine: index, lastLine: index }
      previous = line
    }
  })
  if (current) {
    passages.push(current)
  }
  return passages.filter((passage) => words(passage.text).length >= MIN_PASSAGE_WORDS)
}

function parseNumber(token: string): number | null {
  // "1,234" and "1.234.567" group thousands; any other comma is a decimal mark.
  let normalized = token
  if (/^\d{1,3}(?:,\d{3})+$/.test(token)) {
    normalized = token.replace(/,/g, '')
  } else if (/^\d{1,3}(?:\.\d{3}){2,}$/.test(token)) {
    normalized = token.replace(/\./g, '')
  } else {
    normalized = token.replace(',', '.')
  }
  const value = Number(normalized)
  return Number.isFinite(value) ? value : null
}

/** The numbers a claim states that a reader could check in its source. */
export function claimNumbers(claim: string | null | undefined): Array<number> {
  if (!claim) {
    return []
  }
  const found: Array<number> = []
  for (const match of claim.matchAll(NUMBER_RE)) {
    const token = match[0]
    const value = parseNumber(token)
    if (value == null) {
      continue
    }
    const after = claim.slice((match.index ?? 0) + token.length, (match.index ?? 0) + token.length + 2)
    // A bare small integer ("3 channels") is too common to locate anything.
    if (Number.isInteger(value) && value < 10 && !/^\s?%/.test(after) && !/[.,]/.test(token)) {
      continue
    }
    if (!found.some((seen) => Math.abs(seen - value) < 1e-9)) {
      found.push(value)
    }
  }
  return found
}

function numbersIn(text: string): Array<number> {
  const values: Array<number> = []
  for (const match of text.matchAll(NUMBER_RE)) {
    const value = parseNumber(match[0])
    if (value != null) {
      values.push(value)
    }
  }
  return values
}

function matchedNumbers(text: string, wanted: Array<number>): Array<number> {
  const present = numbersIn(text)
  return wanted.filter((value) => present.some((seen) => Math.abs(seen - value) < 1e-9))
}

function stems(text: string): Set<string> {
  return new Set(
    words(text)
      .filter((word) => word.length >= 5)
      .map((word) => word.toLowerCase().slice(0, 6)),
  )
}

function truncateAtWord(text: string, max: number): string {
  if (text.length <= max) {
    return text
  }
  const cut = text.slice(0, max)
  const space = cut.lastIndexOf(' ')
  return `${(space > max / 2 ? cut.slice(0, space) : cut).replace(/[\s,;:]+$/, '')}${ELLIPSIS}`
}

/** The sentences around the one that carries the claim's figures, within the budget. */
function windowOf(text: string, wanted: Array<number>, claimStems: Set<string>, max: number): string {
  if (text.length <= max) {
    return text
  }
  const sentences = text.split(SENTENCE_SPLIT_RE)
  let best = 0
  let bestScore = -1
  sentences.forEach((sentence, index) => {
    const overlap = [...stems(sentence)].filter((stem) => claimStems.has(stem)).length
    const score = matchedNumbers(sentence, wanted).length * 2 + overlap * 0.5
    if (score > bestScore) {
      best = index
      bestScore = score
    }
  })
  let quote = sentences[best]
  let last = best
  for (let next = best + 1; next < sentences.length; next += 1) {
    const candidate = `${quote} ${sentences[next]}`
    if (candidate.length > max) {
      break
    }
    quote = candidate
    last = next
  }
  const prefix = best > 0 ? `${ELLIPSIS} ` : ''
  if (quote.length > max) {
    return `${prefix}${truncateAtWord(quote, max)}`
  }
  return `${prefix}${quote}${last + 1 < sentences.length ? ` ${ELLIPSIS}` : ''}`
}

/**
 * A slide's number column under its headline: "Gasto hogares" / "$725 MM" /
 * "(+4.3%)" reads back as "Gasto hogares $725 MM (+4.3%)". Only groups that
 * carry a figure the claim states, and not already in the passage, are kept.
 */
function figureGroups(lines: Array<string>, wanted: Array<number>, already: Array<number>): Array<string> {
  const groups: Array<{ label: string | null; parts: Array<string> }> = []
  for (const line of lines) {
    if (!line || line.includes('|') || isBoilerplate(line)) {
      continue
    }
    const isLabel = letterRatio(line) >= 0.5 && words(line).length >= 1 && line.length <= 60
    if (isLabel) {
      groups.push({ label: line, parts: [] })
    } else if (groups.length > 0) {
      groups[groups.length - 1].parts.push(line)
    } else {
      groups.push({ label: null, parts: [line] })
    }
  }
  const missing = wanted.filter((value) => !already.some((seen) => Math.abs(seen - value) < 1e-9))
  return groups
    .filter((group) => group.parts.length > 0 && matchedNumbers(group.parts.join(' '), missing).length > 0)
    .slice(0, MAX_FIGURE_GROUPS)
    .map((group) => [group.label, ...group.parts].filter(Boolean).join(' '))
}

/**
 * The quote a card or the source view shows for a source's raw text, or null
 * when the text holds nothing a reader could follow. With a `claim` (the
 * answer text the citation sits on), it picks the passage that carries the
 * claim's figures, plus the figures themselves when the slide lists them
 * under the passage; without one, the first readable passage.
 */
export function readableQuote(raw: string | null | undefined, claim?: string | null): string | null {
  if (typeof raw !== 'string' || !raw.trim()) {
    return null
  }
  const rawLines = raw.split(/\r?\n/)
  const lines = rawLines.map(cleanLine)
  const bullets = rawLines.map((line) => BULLET_RE.test(line) && !/^\s*#/.test(line))
  const wanted = claimNumbers(claim)
  const claimStems = stems(claim ?? '')
  const filled = lines.filter(Boolean)
  // One line is already a passage (a stored quote, a web excerpt): keep it whole.
  if (filled.length === 1 && !isBoilerplate(filled[0]) && words(filled[0]).length > 0 && letterRatio(filled[0]) >= 0.4) {
    return tidy(windowOf(filled[0], wanted, claimStems, QUOTE_MAX_CHARS + 40))
  }
  const passages = passagesOf(lines, bullets)

  if (passages.length === 0) {
    if (wanted.length === 0) {
      return null
    }
    const groups = figureGroups(lines, wanted, [])
    return groups.length > 0 ? truncateAtWord(groups.join(` ${ELLIPSIS} `), QUOTE_MAX_CHARS) : null
  }

  const scored = passages.map((passage, index) => {
    const zoneEnd = Math.min(
      passages[index + 1]?.firstLine ?? lines.length,
      passage.lastLine + 1 + FIGURE_ZONE_LINES,
    )
    const zone = lines.slice(passage.lastLine + 1, zoneEnd)
    const inPassage = matchedNumbers(passage.text, wanted)
    const inZone = matchedNumbers(zone.join('\n'), wanted).filter((value) => !inPassage.includes(value))
    const overlap = [...stems(passage.text)].filter((stem) => claimStems.has(stem)).length
    // With nothing to match, a full sentence beats a run of chart labels.
    // A passage opening mid-sentence (lower case) is the tail of an earlier chunk.
    const shape = Math.min(words(passage.text).length / 20, 1) + (TERMINAL_RE.test(passage.text) ? 1 : 0)
      + (UPPER_START_RE.test(passage.text) ? 1 : 0)
    return {
      passage, zone, inPassage,
      score: inPassage.length * 2 + inZone.length + overlap * 0.5 + shape * 0.25,
    }
  })
  const best = scored.reduce((top, entry) => (entry.score > top.score ? entry : top), scored[0])

  const groups = figureGroups(best.zone, wanted, best.inPassage)
  const groupText = groups.join(` ${ELLIPSIS} `)
  const budget = groupText ? Math.max(QUOTE_MAX_CHARS - groupText.length - 3, QUOTE_MAX_CHARS / 2) : QUOTE_MAX_CHARS
  const passageText = windowOf(best.passage.text, wanted, claimStems, budget)
  const quote = groupText ? `${passageText.replace(new RegExp(`\\s*${ELLIPSIS}$`), '')} ${ELLIPSIS} ${groupText}` : passageText
  return tidy(truncateAtWord(quote, QUOTE_MAX_CHARS + 40))
}

/**
 * The figure a claim cites, by the percentages it states, when the answer did
 * not name one: the first stated percentage that exactly one percent figure
 * rounds to. Null when none matches unambiguously.
 */
export function figureIndexForClaim(
  figures: Array<CitationFigure> | null | undefined,
  claim: string | null | undefined,
): number | null {
  if (!figures || figures.length === 0 || !claim) {
    return null
  }
  for (const match of claim.matchAll(PERCENT_MENTION_RE)) {
    const token = match[1]
    const stated = parseNumber(token)
    if (stated == null) {
      continue
    }
    const decimals = (token.split(/[.,]/)[1] ?? '').length
    const factor = 10 ** decimals
    const candidates = figures
      .map((figure, index) => ({ figure, index }))
      .filter(({ figure }) => ['percent', '%', 'percentage', 'pct'].includes(figure.unit.trim().toLowerCase()))
      .filter(({ figure }) => Math.abs(Math.round(figure.value * factor) / factor - stated) < 1e-9)
    if (candidates.length === 1) {
      return candidates[0].index
    }
  }
  return null
}

/**
 * The answer text a citation point sits on: its line (a paragraph, a bullet,
 * a table row) up to the point, with earlier citation links removed.
 */
export function claimBefore(text: string, offset: number | null | undefined): string | null {
  if (offset == null || !Number.isInteger(offset) || offset < 0) {
    return null
  }
  const head = text.slice(0, Math.min(offset, text.length))
  const lineStart = head.lastIndexOf('\n') + 1
  const claim = head.slice(lineStart).replace(LIST_MARKER_RE, '').replace(CITE_LINK_RE, ' ').replace(/[*_`#>]/g, '').replace(/\s+/g, ' ').trim()
  return claim || null
}
